import React, { useCallback, useEffect, useState } from 'react'
import {
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  ClockIcon,
  ArrowPathIcon,
  XMarkIcon,
  LinkIcon,
  EyeIcon,
} from '@heroicons/react/24/outline'
import { Button } from '../../../components/ui/button'
import { getLocalApiUrl, authFetch } from '../../../config/api'

const STATUS_STYLES = {
  sent: { label: 'Sent', bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  opened: { label: 'Opened', bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
  submitted: { label: 'Submitted', bg: '#ecfdf5', color: '#047857', border: '#a7f3d0' },
  meeting_requested: { label: 'Meeting requested', bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
  overdue: { label: 'Overdue', bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
  cancelled: { label: 'Cancelled', bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' },
  closed: { label: 'Closed', bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' },
}

function fmtDate(v) {
  if (!v) return '—'
  try {
    return new Date(v).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return String(v)
  }
}

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.sent
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold border"
      style={{ background: s.bg, color: s.color, borderColor: s.border }}
    >
      {s.label}
    </span>
  )
}

export default function EcddSowOutreachPanel({ ecddId, readOnly = false }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [sendOpen, setSendOpen] = useState(false)
  const [contact, setContact] = useState(null)
  const [channels, setChannels] = useState('email')
  const [emailTo, setEmailTo] = useState('')
  const [smsTo, setSmsTo] = useState('')
  const [sending, setSending] = useState(false)
  const [busyId, setBusyId] = useState(null)
  const [lastFormUrl, setLastFormUrl] = useState('')
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')

  const load = useCallback(async () => {
    if (!ecddId) return
    setLoading(true)
    setError('')
    try {
      const res = await authFetch(getLocalApiUrl(`/api/ecdd/${encodeURIComponent(ecddId)}/outreach`))
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Failed to load outreach')
      setItems(Array.isArray(j.data) ? j.data : [])
    } catch (e) {
      setError(e.message || 'Failed to load outreach')
    } finally {
      setLoading(false)
    }
  }, [ecddId])

  useEffect(() => {
    load()
  }, [load])

  const openSend = async () => {
    setError('')
    setInfo('')
    setLastFormUrl('')
    setSendOpen(true)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/ecdd/${encodeURIComponent(ecddId)}/outreach/contact`)
      )
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Failed to load contact')
      setContact(j.data)
      setEmailTo(j.data?.email || '')
      setSmsTo(j.data?.phone || '')
      if (j.data?.email && j.data?.phone) setChannels('email')
      else if (j.data?.phone && !j.data?.email) setChannels('sms')
      else setChannels('email')
    } catch (e) {
      setError(e.message || 'Failed to load contact')
    }
  }

  const handleSend = async () => {
    setSending(true)
    setError('')
    setInfo('')
    try {
      const res = await authFetch(getLocalApiUrl(`/api/ecdd/${encodeURIComponent(ecddId)}/outreach`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          channels,
          emailTo: emailTo.trim() || undefined,
          smsTo: smsTo.trim() || undefined,
          tatDays: 5,
        }),
      })
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Failed to send')
      setLastFormUrl(j.formUrl || '')
      const skipped = []
      if (j.delivery?.email?.skipped) skipped.push(`email (${j.delivery.email.reason})`)
      if (j.delivery?.sms?.skipped) skipped.push(`SMS (${j.delivery.sms.reason})`)
      setInfo(
        skipped.length
          ? `Request created. Delivery skipped for: ${skipped.join(', ')}. Copy the form link below.`
          : 'Request sent to patron.'
      )
      setSendOpen(false)
      await load()
    } catch (e) {
      setError(e.message || 'Failed to send')
    } finally {
      setSending(false)
    }
  }

  const remind = async (requestId) => {
    setBusyId(requestId)
    setError('')
    try {
      const res = await authFetch(
        getLocalApiUrl(
          `/api/ecdd/${encodeURIComponent(ecddId)}/outreach/${encodeURIComponent(requestId)}/remind`
        ),
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }
      )
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Reminder failed')
      if (j.formUrl) setLastFormUrl(j.formUrl)
      setInfo('Reminder sent.')
      await load()
    } catch (e) {
      setError(e.message || 'Reminder failed')
    } finally {
      setBusyId(null)
    }
  }

  const cancel = async (requestId) => {
    if (!window.confirm('Cancel this patron outreach request?')) return
    setBusyId(requestId)
    setError('')
    try {
      const res = await authFetch(
        getLocalApiUrl(
          `/api/ecdd/${encodeURIComponent(ecddId)}/outreach/${encodeURIComponent(requestId)}/cancel`
        ),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: 'Cancelled by staff' }),
        }
      )
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Cancel failed')
      setInfo('Request cancelled.')
      await load()
    } catch (e) {
      setError(e.message || 'Cancel failed')
    } finally {
      setBusyId(null)
    }
  }

  const openItems = items.filter((x) =>
    ['sent', 'opened', 'overdue'].includes(x.status)
  )
  const latest = items[0]

  return (
    <div className="rounded-lg border border-teal-200 bg-teal-50/40 px-3.5 py-3 mb-4">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
        <div>
          <div className="text-[13px] font-semibold text-teal-900">Patron outreach — Source of Wealth</div>
          <p className="text-xs text-teal-800/80 m-0 mt-0.5">
            Send a form by email or SMS. Patron can fill SoW or request a meeting. Default TAT: 5 days.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-teal-900"
            onClick={() => window.open('/outreach/sow?preview=1', '_blank', 'noopener,noreferrer')}
          >
            <EyeIcon className="h-3.5 w-3.5 mr-1" />
            Preview form
          </Button>
          {!readOnly && (
            <Button type="button" size="sm" variant="outline" className="border-teal-300 text-teal-900" onClick={openSend}>
              Request from patron
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5 mb-2">{error}</div>
      )}
      {info && (
        <div className="text-xs text-teal-900 bg-white/80 border border-teal-200 rounded px-2 py-1.5 mb-2">{info}</div>
      )}
      {lastFormUrl && (
        <div className="flex flex-wrap items-center gap-2 text-xs bg-white border border-slate-200 rounded px-2 py-1.5 mb-2">
          <LinkIcon className="h-3.5 w-3.5 text-slate-500 shrink-0" />
          <span className="truncate flex-1 min-w-0 text-slate-600">{lastFormUrl}</span>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            onClick={() => window.open(lastFormUrl, '_blank', 'noopener,noreferrer')}
          >
            <EyeIcon className="h-3.5 w-3.5 mr-1" />
            Open link
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            onClick={() => navigator.clipboard?.writeText(lastFormUrl)}
          >
            Copy link
          </Button>
        </div>
      )}

      {loading && <p className="text-xs text-slate-500 m-0">Loading outreach…</p>}

      {!loading && !items.length && (
        <p className="text-xs text-slate-500 m-0">No SoW request sent yet for this ECDD.</p>
      )}

      {!loading && latest && (
        <div className="space-y-2">
          {(openItems.length ? openItems : [latest]).map((r) => (
            <div
              key={r.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs"
            >
              <StatusPill status={r.status} />
              <span className="inline-flex items-center gap-1 text-slate-600">
                {(r.channels === 'email' || r.channels === 'both') && (
                  <EnvelopeIcon className="h-3.5 w-3.5" title={r.emailTo || 'email'} />
                )}
                {(r.channels === 'sms' || r.channels === 'both') && (
                  <DevicePhoneMobileIcon className="h-3.5 w-3.5" title={r.smsTo || 'sms'} />
                )}
                <span className="capitalize">{r.channels}</span>
              </span>
              <span className="inline-flex items-center gap-1 text-slate-600">
                <ClockIcon className="h-3.5 w-3.5" />
                Due {fmtDate(r.dueAt)}
                {typeof r.daysRemaining === 'number' &&
                  ['sent', 'opened', 'overdue'].includes(r.status) && (
                    <span className={r.isOverdue ? 'text-red-600 font-semibold' : 'text-slate-500'}>
                      ({r.isOverdue ? `${Math.abs(r.daysRemaining)}d overdue` : `${r.daysRemaining}d left`})
                    </span>
                  )}
              </span>
              {r.responseKind && (
                <span className="text-slate-600">
                  Response: {r.responseKind === 'meeting' ? 'meeting' : 'SoW form'}
                </span>
              )}
              {r.reminderCount > 0 && (
                <span className="text-slate-500">Reminders: {r.reminderCount}</span>
              )}
              {!readOnly && ['sent', 'opened', 'overdue'].includes(r.status) && (
                <span className="ml-auto flex gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    disabled={busyId === r.id}
                    onClick={() => remind(r.id)}
                  >
                    <ArrowPathIcon className="h-3.5 w-3.5 mr-1" />
                    Remind
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs text-slate-500"
                    disabled={busyId === r.id}
                    onClick={() => cancel(r.id)}
                  >
                    <XMarkIcon className="h-3.5 w-3.5 mr-1" />
                    Cancel
                  </Button>
                </span>
              )}
            </div>
          ))}
          {items.length > 1 && (
            <button type="button" className="text-[11px] text-teal-800 underline" onClick={load}>
              Refresh ({items.length} total)
            </button>
          )}
        </div>
      )}

      {sendOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white shadow-xl border border-slate-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-900 m-0">Request SoW from patron</h3>
              <button type="button" className="text-slate-400 hover:text-slate-600" onClick={() => setSendOpen(false)}>
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="px-4 py-3 space-y-3">
              <p className="text-xs text-slate-500 m-0">
                {contact?.patronName || 'Patron'} — due in 5 days. Patron can complete the form or request a meeting.
              </p>
              <div>
                <div className="text-xs font-medium text-slate-700 mb-1.5">Channel</div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'email', label: 'Email' },
                    { id: 'sms', label: 'SMS' },
                    { id: 'both', label: 'Both' },
                  ].map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setChannels(c.id)}
                      className={`rounded-full px-3 py-1 text-xs font-medium border ${
                        channels === c.id
                          ? 'bg-teal-700 text-white border-teal-700'
                          : 'bg-white text-slate-700 border-slate-200'
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
              {(channels === 'email' || channels === 'both') && (
                <label className="block">
                  <span className="text-xs font-medium text-slate-700">Email</span>
                  <input
                    className="mt-1 w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm"
                    value={emailTo}
                    onChange={(e) => setEmailTo(e.target.value)}
                    placeholder="patron@example.com"
                  />
                </label>
              )}
              {(channels === 'sms' || channels === 'both') && (
                <label className="block">
                  <span className="text-xs font-medium text-slate-700">Mobile</span>
                  <input
                    className="mt-1 w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm"
                    value={smsTo}
                    onChange={(e) => setSmsTo(e.target.value)}
                    placeholder="+614xxxxxxxx"
                  />
                </label>
              )}
              {error && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">{error}</div>
              )}
            </div>
            <div className="flex justify-between gap-2 px-4 py-3 border-t border-slate-100">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-teal-800"
                onClick={() => window.open('/outreach/sow?preview=1', '_blank', 'noopener,noreferrer')}
              >
                <EyeIcon className="h-3.5 w-3.5 mr-1" />
                Preview form
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={() => setSendOpen(false)}>
                  Cancel
                </Button>
                <Button type="button" size="sm" disabled={sending} onClick={handleSend}>
                  {sending ? 'Sending…' : 'Send request'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
